<template>
  <Flex
    direction="column"
    align="center"
  >
    <img
      v-if="$screen !== 's'"
      src="/img/cancel-subscription/treatwell_offer.svg"
      :class="$style.image"
    >
    <BaseHeading size="l">
      {{ $t(
          'subscription.cancelation_flow.success_treatwell_offer.description_updated'
        ) }}
    </BaseHeading>
    <BaseSpinner
      v-if="loading"
      inline
    />
    <BaseText
      v-else
      v-test="'cancel-flow-try-for-free-description'"
    >
      {{ $t(
          'subscription.cancelation_flow.success_treatwell_offer.description_activation',
          {
            date: filters.date(
              dayjs(activeSubscription?.periodEnd),
              { format: 'long' }
            )
          }
        ) }}
    </BaseText>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import type { StripeSubscription } from '@/types';
import { StripeSubscriptionStatus } from '@/types';
import { GET_STRIPE_SUBSCRIPTIONS_QUERY } from '../graphql';

interface StripeSubscriptionsQueryResult {
  stripeSubscriptions: StripeSubscription[];
}

const { result: stripeSubscriptionsData, loading } =
  useQuery<StripeSubscriptionsQueryResult>(GET_STRIPE_SUBSCRIPTIONS_QUERY);

const activeSubscription = computed(() => {
  if (loading.value) {
    return null;
  }
  const subscriptions = stripeSubscriptionsData.value?.stripeSubscriptions;
  if (!subscriptions || subscriptions.length === 0) {
    return null;
  }
  const foundSubscription = subscriptions.find(
    (sub: StripeSubscription) => sub.status === StripeSubscriptionStatus.Active,
  );

  if (!foundSubscription || !foundSubscription.periodEnd) {
    return null;
  }

  const sevenDaysFromNow = dayjs().add(7, 'days');
  const originalPeriodEnd = dayjs(foundSubscription.periodEnd);

  // Create a new object to avoid mutating the original subscription from the cache
  // and to allow adjustment of periodEnd.
  const adjustedSubscription: StripeSubscription = {
    ...foundSubscription,
    periodEnd: originalPeriodEnd.isBefore(sevenDaysFromNow)
      ? sevenDaysFromNow.toISOString()
      : foundSubscription.periodEnd,
  };

  return adjustedSubscription;
});
</script>

<style
  lang="scss"
  module
>
.image {
  margin-bottom: $spacing;
  height: 10rem;
  width: auto;
}
</style>
